import gql from "graphql-tag"
import {
  CartUserErrorsFields,
  CartFields,
  CustomerUserErrorsFields,
  CustomerAccessTokenFields,
  CustomerFields,
} from "./fragments"

export const CreateCart = gql`
  ${CartFields}
  ${CartUserErrorsFields}
  mutation CreateCart(
    $buyerIdentity: CartBuyerIdentityInput
    $lines: [CartLineInput!]
    $note: String!
  ) {
    cartCreate(
      input: { buyerIdentity: $buyerIdentity, lines: $lines, note: $note }
    ) {
      cart {
        ...CartFields
      }
      userErrors {
        ...CartUserErrorsFields
      }
    }
  }
`

export const AddCartLine = gql`
  ${CartFields}
  ${CartUserErrorsFields}
  mutation AddCartLine($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFields
      }
      userErrors {
        ...CartUserErrorsFields
      }
    }
  }
`

export const RemoveCartLine = gql`
  ${CartFields}
  ${CartUserErrorsFields}
  mutation RemoveCartLine($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ...CartFields
      }
      userErrors {
        ...CartUserErrorsFields
      }
    }
  }
`

export const UpdateCartLine = gql`
  ${CartFields}
  ${CartUserErrorsFields}
  mutation UpdateCartLine($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFields
      }
      userErrors {
        ...CartUserErrorsFields
      }
    }
  }
`

export const UpdateCheckout = gql`
  ${CartFields}
  ${CartUserErrorsFields}
  mutation UpdateCheckout($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFields
      }
      userErrors {
        ...CartUserErrorsFields
      }
    }
  }
`

export const ApplyDiscount = gql`
  ${CartFields}
  ${CartUserErrorsFields}
  mutation ApplyDiscount($cartId: ID!, $discountCode: String!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: [$discountCode]) {
      cart {
        ...CartFields
      }
      userErrors {
        ...CartUserErrorsFields
      }
    }
  }
`

export const RemoveDiscount = gql`
  ${CartFields}
  ${CartUserErrorsFields}
  mutation RemoveDiscount($cartId: ID!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: []) {
      cart {
        ...CartFields
      }
      userErrors {
        ...CartUserErrorsFields
      }
    }
  }
`

export const CreateAccount = gql`
  ${CustomerFields}
  ${CustomerUserErrorsFields}
  mutation CreateAccount($input: CustomerCreateInput!) {
    customerCreate(input: $input) {
      customer {
        ...CustomerFields
      }
      customerUserErrors {
        ...CustomerUserErrorsFields
      }
    }
  }
`

export const RecoverAccount = gql`
  ${CustomerUserErrorsFields}
  mutation RecoverAccount($email: String!) {
    customerRecover(email: $email) {
      customerUserErrors {
        ...CustomerUserErrorsFields
      }
    }
  }
`

export const ResetAccount = gql`
  ${CustomerFields}
  ${CustomerAccessTokenFields}
  ${CustomerUserErrorsFields}
  mutation ResetAccount($id: ID!, $input: CustomerResetInput!) {
    customerReset(id: $id, input: $input) {
      customer {
        ...CustomerFields
      }
      customerAccessToken {
        ...CustomerAccessTokenFields
      }
      customerUserErrors {
        ...CustomerUserErrorsFields
      }
    }
  }
`

export const ResetAccountByUrl = gql`
  ${CustomerFields}
  ${CustomerAccessTokenFields}
  ${CustomerUserErrorsFields}
  mutation ResetAccountByUrl($password: String!, $resetUrl: URL!) {
    customerResetByUrl(password: $password, resetUrl: $resetUrl) {
      customer {
        ...CustomerFields
      }
      customerAccessToken {
        ...CustomerAccessTokenFields
      }
      customerUserErrors {
        ...CustomerUserErrorsFields
      }
    }
  }
`

export const CreateCustomerAccessToken = gql`
  mutation CreateCustomerAccessToken($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerAccessToken {
        accessToken
      }
      customerUserErrors {
        message
      }
    }
  }
`

export const GetCustomerAccessToken = gql`
  mutation GetCustomerAccessToken($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      userErrors {
        field
        message
      }
      customerAccessToken {
        accessToken
        expiresAt
      }
    }
  }
`

export const DeleteCustomerAccessToken = gql`
  mutation DeleteCustomerAccessToken($customerAccessToken: String!) {
    customerAccessTokenDelete(customerAccessToken: $customerAccessToken) {
      deletedAccessToken
      deletedCustomerAccessTokenId
      userErrors {
        field
        message
      }
    }
  }
`

export const AssociateCart = gql`
  ${CartUserErrorsFields}
  mutation CartBuyerIdentityUpdate(
    $cartId: ID!
    $customerAccessToken: String!
  ) {
    cartBuyerIdentityUpdate(
      cartId: $cartId
      buyerIdentity: { customerAccessToken: $customerAccessToken }
    ) {
      cart {
        id
      }
      userErrors {
        ...CartUserErrorsFields
      }
    }
  }
`

export const CustomerAddressCreate = gql`
  ${CustomerUserErrorsFields}
  mutation CustomerAddressCreate(
    $customerAccessToken: String!
    $address: MailingAddressInput!
  ) {
    customerAddressCreate(
      customerAccessToken: $customerAccessToken
      address: $address
    ) {
      customerAddress {
        id
      }
      customerUserErrors {
        ...CustomerUserErrorsFields
      }
    }
  }
`

export const CustomerAddressUpdate = gql`
  ${CustomerUserErrorsFields}
  mutation CustomerAddressUpdate(
    $customerAccessToken: String!
    $id: ID!
    $address: MailingAddressInput!
  ) {
    customerAddressUpdate(
      customerAccessToken: $customerAccessToken
      id: $id
      address: $address
    ) {
      customerAddress {
        id
      }
      customerUserErrors {
        ...CustomerUserErrorsFields
      }
    }
  }
`

export const CustomerAddressDelete = gql`
  ${CustomerUserErrorsFields}
  mutation CustomerAddressDelete($id: ID!, $customerAccessToken: String!) {
    customerAddressDelete(id: $id, customerAccessToken: $customerAccessToken) {
      customerUserErrors {
        ...CustomerUserErrorsFields
      }
      deletedCustomerAddressId
    }
  }
`

export const CustomerDefaultAddressUpdate = gql`
  ${CustomerUserErrorsFields}
  mutation CustomerDefaultAddressUpdate(
    $customerAccessToken: String!
    $addressId: ID!
  ) {
    customerDefaultAddressUpdate(
      customerAccessToken: $customerAccessToken
      addressId: $addressId
    ) {
      customer {
        id
      }
      customerUserErrors {
        ...CustomerUserErrorsFields
      }
    }
  }
`
